import { useEffect, useRef, useMemo, useState } from 'react';
import { useLocation, NavLink, useNavigate } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import queryString from 'query-string';
import {
  IconLoader,
  Table,
  useQueryState,
  Filters,
  useFilters,
  PermissionEnum,
  usePrevious,
  ButtonPrimary,
  ButtonSize,
  IconExpand,
  useGetList,
  BaseTransaction,
  SchemaFilterType,
  BaseellaStatus,
  ButtonSecondary,
  SignaturePopup,
  ButtonPseudo,
  IconDownload,
  SelectItemProps,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useBatchCreateTransactionSignatureMutation,
  useSendCodeMutation,
  useValidateCodeMutation,
} from '../../store/signature/service';
import { MfaToken, MfaType } from '../../store/types';
import {
  useLazyGetExchangeListQuery,
  useLazyGetExchangeListStatusesQuery,
  useExportExchangeTransactionMutation,
} from './store/services';
import {
  clearExchangeListState,
  setExpandedTransactionId,
  setSelectedTake,
} from './store/slices/list';
import { setNotificationVisible } from '../../../../../store/slice';
import {
  columns,
  columnsLabels,
  expandableColumns,
  filtersSchema,
  schema,
} from './variables/list';
import { useLazyGetGeneralLedgerTransactionValuesQuery } from '../transactions/store/services';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import formatExchangeList from './helpers/format-list';
import ROUTES from '../../routes';

import cn from './styles.module.css';
import { clearMfaToken } from '../../store/signature/slice';

let timeout: NodeJS.Timeout;
let timer: NodeJS.Timeout;

const defaultInprogressStatuses = [
  BaseellaStatus.DRAFT,
  BaseellaStatus.NONE,
  BaseellaStatus.NEW,
  BaseellaStatus.HELD,
  BaseellaStatus.SENT,
  BaseellaStatus.PROC,
  BaseellaStatus.ARCH,
  BaseellaStatus.REV,
  BaseellaStatus.AML,
  BaseellaStatus.AML_INFO,
  BaseellaStatus.AML_DONE,
  BaseellaStatus.READY_TO_SEND,
  BaseellaStatus.READY_TO_PSP,
  BaseellaStatus.ACCP_OUT,
  BaseellaStatus.BOOKED,
];

const ExchangeList = (): React.JSX.Element => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    root: { settings, userId },
    exchange: {
      list: {
        transactions,
        pagination,
        expandedTransactionId,
        collapseAllRows,
        selectedTake,
        errorsList,
      },
    },
    transactionsDictionaries: {
      currencies,
      activePermissions,
      rateSources,
      currenciesList,
    },
    signatures: { mfaToken },
  } = useAppSelector((state) => state);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [onlyRemoved, setOnlyRemoved] = useState(false);

  const [statusFilterState, setStatusFilterState] = useState<
    'in-progress' | 'unsigned' | 'rejected' | 'all' | 'rate-request' | undefined
  >(undefined);
  const [savedFilters, setSavedFilters] = useState<
    {
      id: string;
      name: string;
      value: Record<string, any>;
      default?: boolean;
      employeeId: string;
    }[]
  >([]);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState('');

  const [sendCode, { isLoading: sendCodeInProgress }] = useSendCodeMutation();
  const [validateCode, { isLoading: validateCodeInProgress }] =
    useValidateCodeMutation();
  const [
    createSignature,
    {
      isLoading: createSignatureInProgress,
      isSuccess: createSignatureIsSuccess,
    },
  ] = useBatchCreateTransactionSignatureMutation();

  const [getList, { isLoading, isFetching: exchangeListFetching }] =
    useLazyGetExchangeListQuery();

  const [unsignedTransactionsList, setUnsignedTransactionsList] = useState<
    string[]
  >([]);

  const [signaturePopupVisible, setSignaturePopupVisible] = useState(false);
  const [errorMessageSignature, setErrorMessageSignature] = useState('');
  const [disabledRowIds, setDisabledRowIds] = useState<string[]>([]);

  const prevTransactions = usePrevious(transactions);

  const {
    autocompleteList: accounts,
    onSearch,
    onClear,
  } = useGetAccountsAutocompleteList();

  const {
    autocompleteList: recipientAccounts,
    onSearch: recipientOnSearch,
    onClear: recipientOnClear,
  } = useGetAccountsAutocompleteList();

  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);
  const [getExchangeStatuses] = useLazyGetExchangeListStatusesQuery();

  const [
    exportTransaction,
    { isSuccess: exportIsSuccess, isLoading: exportIsLoading },
  ] = useExportExchangeTransactionMutation();

  const onExportClick = async () => {
    const exportFilters: Record<string, string | number | string[]> = {};

    if (filters && Object.keys(filters)?.length) {
      Object.keys(filters)?.forEach((item) => {
        if (item !== 'cursor') {
          if (item !== 'transferDate') {
            if (
              item === 'consumerStatus' ||
              item === 'baseellaStatus' ||
              item === 'monitoringStatus' ||
              item === 'benificiaryType' ||
              item === 'chargeType' ||
              item === 'id' ||
              item === 'senderUserId' ||
              item === 'recipientUserId' ||
              item === 'currencyId' ||
              item === 'typeId' ||
              item === 'excludeAccounts'
            ) {
              exportFilters[item] = filters[item].value;
            } else {
              const [filterValue] = filters[item].value;
              exportFilters[item] = filterValue;
            }
          } else if (item === 'transferDate') {
            const transferDate = filters[item].value;

            const [from, to] = transferDate;

            exportFilters.transferDateFrom = from;

            if (to) {
              exportFilters.transferDateTo = to;
            }
          }
        }
      });
    }

    await exportTransaction(exportFilters);
  };

  const [
    getGeneralLedgerTransactionValues,
    { isLoading: glIsLoading, isFetching, isSuccess },
  ] = useLazyGetGeneralLedgerTransactionValuesQuery();

  const prevFilters = usePrevious(filters);

  const getGeneralLedgerAction = (id: string) => {
    dispatch(setExpandedTransactionId(id));
    getGeneralLedgerTransactionValues(id);
  };

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<BaseTransaction>({
      getList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSendCode = async () => {
    clearTimeout(timer);

    const response = (await sendCode({ mfa: MfaType.TOTP })) as {
      data: { data: MfaToken };
    };

    if (response && response.data && response.data.data) {
      setSignaturePopupVisible(true);

      const expireTime = Math.round(
        // eslint-disable-next-line react-hooks/purity
        new Date(response.data.data.expiresAt).getTime() - Date.now(),
      );

      timer = setTimeout(() => {
        onSendCode();
      }, expireTime);
    }
  };

  const validateCodeAction = async (code: string) => {
    clearTimeout(timer);
    setErrorMessageSignature('');

    if (code) {
      const response = (await validateCode({
        mfaToken: mfaToken as string,
        totpToken: code,
      })) as {
        data?: { data: MfaToken };
        error: { data: { message: string; statusCode: number } };
      };

      if (response && response.data && response.data.data) {
        const createResponse = (await createSignature({
          mfaToken: response.data.data.token,
          transactionId: unsignedTransactionsList,
        })) as {
          error: { data: { message: string }; status: number };
        };

        if (
          createResponse &&
          createResponse.error &&
          createResponse.error.data
        ) {
          if (createResponse.error?.status === 403) {
            setErrorMessageSignature('Access denied');
          } else {
            // eslint-disable-next-line no-lonely-if
            if (!Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(createResponse.error.data.message);
            } else if (Array.isArray(createResponse.error.data.message)) {
              setErrorMessageSignature(
                'Internal error. Transaction wasn`t signed',
              );
            }
          }
        }
      } else if (response && response.error && response.error.data) {
        if (response.error?.data?.statusCode === 403) {
          setErrorMessageSignature('Access denied');
        } else if (response.error.data.statusCode === 401) {
          setErrorMessageSignature('Incorrect OTP code');
        } else {
          setErrorMessageSignature(response.error.data.message);
        }
      }
    } else {
      setErrorMessageSignature('OTP Code should not be empty');
    }
  };

  const onSavedFilterClick = (id: string, savedFilter: Filters) => {
    setStatusFilterState(undefined);

    const filterValues: Filters = {
      ...savedFilter,
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    onFilter(filterValues);
    setSelectedSavedFilter(id);
  };

  const onInProgressClick = () => {
    setStatusFilterState('in-progress');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: defaultInprogressStatuses,
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const onUnsignedClick = () => {
    setStatusFilterState('unsigned');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.NOT_SIGNED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const onRejectedClick = () => {
    setStatusFilterState('rejected');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.AML_REJ, BaseellaStatus.REJECTED],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const onRateRequestClick = () => {
    setStatusFilterState('rate-request');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [BaseellaStatus.FX_RATE_REQUEST],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const onAllClick = () => {
    setStatusFilterState('all');

    const nextFilters = { ...filters };

    const currentSavedFilter = savedFilters?.find(
      (item) => item.id === selectedSavedFilter,
    );

    if (currentSavedFilter) {
      Object.keys(currentSavedFilter?.value)?.forEach((key) => {
        delete nextFilters[key];
      });
    }

    const filterValues: Filters = {
      ...nextFilters,
      baseellaStatus: {
        value: [],
        type: SchemaFilterType.STRING,
      },
      take: {
        value: [(selectedTake as number)?.toString()],
        type: SchemaFilterType.STRING,
      },
    };

    if (includeRemoved) {
      filterValues.includeRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    if (onlyRemoved) {
      filterValues.onlyRemoved = {
        value: ['true'],
        type: SchemaFilterType.STRING,
      };
    }

    setSelectedSavedFilter('');
    onFilter(filterValues);
  };

  const handleClose = () => {
    setSignaturePopupVisible(false);
    setErrorMessageSignature('');
    dispatch(clearMfaToken());
  };

  const onDuplicate = (id: string) => {
    const currentTransaction = transactions?.find((item) => item.id === id);

    if (currentTransaction) {
      navigate(`${ROUTES.EXCHANGE_CREATE}${search || ''}`, {
        state: { ...currentTransaction, duplicate: true },
      });
    }
  };

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'senderAccountNumber') {
      onSearch(searchAutocomplete);
    }

    if (name === 'recipientAccountNumber') {
      recipientOnSearch(searchAutocomplete);
    }
  };

  const onClearAutoComplete = () => {
    onClear();

    setTimeout(() => {
      recipientOnClear();
    }, 100);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange,
    onSearchAutocomplete,
  });

  const onClearTimeout = async () => {
    await clearTimeout(timeout);
  };

  const onGetStatuses = async (query: string) => {
    await onClearTimeout();

    timeout = setTimeout(async () => {
      await getExchangeStatuses(query);
      await onGetStatuses(query);
    }, 5000);
  };

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setSelectedTake(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (exportIsSuccess) {
      dispatch(
        setNotificationVisible({
          visible: true,
          text: "Report in progress. You'll be notified.",
          type: 'exchange',
        }),
      );
    }
  }, [exportIsSuccess]);

  useEffect(() => {
    setValues('senderAccountNumber', accounts);
    setValues('recipientAccountNumber', recipientAccounts);
    setValues('exchangeRateSourceId', rateSources as SelectItemProps[]);
    setValues('exchangeCurrencyFromId', currencies as SelectItemProps[]);
    setValues('exchangeCurrencyToId', currencies as SelectItemProps[]);
  }, [accounts, currencies, rateSources, currencies, recipientAccounts]);

  useEffect(() => {
    if (
      selectedTake &&
      selectedTake !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !transactions &&
      sort &&
      sort.sortProp
    ) {
      let parsed;

      if (search) {
        parsed = queryString.parse(search);
      }

      let filtersValues: any = {
        take: {
          value: [selectedTake?.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      const savedFiltersJSON = localStorage.getItem('exchange-filters');

      let parsedSavedFilters: {
        id: string;
        name: string;
        value: Record<string, any>;
        default?: boolean;
      }[];

      let defaultSavedFilter;

      if (!Object.keys(filters)?.length) {
        if (savedFiltersJSON) {
          parsedSavedFilters = JSON.parse(savedFiltersJSON);

          if (parsedSavedFilters && parsedSavedFilters?.length) {
            const currentUserParsedFilters = parsedSavedFilters?.filter(
              (item: any) => item.employeeId === userId,
            );

            if (currentUserParsedFilters && currentUserParsedFilters?.length) {
              defaultSavedFilter = currentUserParsedFilters?.find(
                (item) => item.default,
              );
            }
          }
        }

        if (defaultSavedFilter) {
          setSelectedSavedFilter(defaultSavedFilter.id);

          filtersValues = {
            ...filtersValues,
            ...defaultSavedFilter?.value,
          };
        } else {
          filtersValues = {
            ...filtersValues,
            ...filters,
          };
        }
      } else {
        filtersValues = {
          ...filtersValues,
          ...filters,
        };
      }

      if (!filtersValues.baseellaStatus) {
        filtersValues.baseellaStatus = {
          value: defaultInprogressStatuses,
          type: SchemaFilterType.STRING,
        };

        setStatusFilterState('in-progress');
      } else if (filtersValues?.baseellaStatus?.value && !defaultSavedFilter) {
        const sortedDefaultStatusesInProgress = defaultInprogressStatuses.sort(
          (a, b) => a?.localeCompare(b),
        );

        const sortedValues = filtersValues?.baseellaStatus?.value.sort(
          (a: any, b: any) => a?.localeCompare(b),
        );

        if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
          setStatusFilterState('in-progress');
        } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
          setStatusFilterState('unsigned');
        } else if (
          isEqual(sortedValues, [
            BaseellaStatus.AML_REJ,
            BaseellaStatus.REJECTED,
          ])
        ) {
          setStatusFilterState('rejected');
        } else {
          setStatusFilterState('all');
        }
      }

      if (parsed && parsed.includeRemoved) {
        setIncludeRemoved(true);

        filtersValues.includeRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      if (parsed && parsed.onlyRemoved) {
        setOnlyRemoved(true);

        filtersValues.onlyRemoved = {
          value: ['true'],
          type: SchemaFilterType.STRING,
        };
      }

      getListAction({
        filters: filtersValues,
        sort,
      });
    }
  }, [filters, sort, transactions, selectedTake, userId]);

  useEffect(() => {
    if (selectedSavedFilter && statusFilterState) {
      setStatusFilterState(undefined);
    }
  }, [selectedSavedFilter, statusFilterState]);

  useEffect(
    () => () => {
      setTimeout(async () => {
        await onClearTimeout();
      });

      dispatch(clearExchangeListState());
    },
    [dispatch],
  );

  useEffect(() => {
    if (createSignatureIsSuccess) {
      const nextDisabledRowIds = ([] as string[]).concat(disabledRowIds);

      unsignedTransactionsList.forEach((item) => {
        nextDisabledRowIds.push(item);
      });

      setDisabledRowIds(nextDisabledRowIds);

      handleClose();

      if (transactions && transactions.length) {
        const ids = transactions.map((item) => item.id);
        const query = `?id=${ids.join('&id=')}`;

        setTimeout(() => {
          onGetStatuses(query);
        }, 300);
      }
    }
  }, [createSignatureIsSuccess, transactions]);

  useEffect(() => {
    if (transactions) {
      setDisabledRowIds(
        transactions
          ?.filter((item) => item.baseellaStatus !== BaseellaStatus.NOT_SIGNED)
          ?.map((item) => item.id),
      );
    }
  }, [transactions]);

  useEffect(() => {
    if (transactions && transactions.length) {
      const ids = transactions.map((item) => item.id);
      const query = `?id=${ids.join('&id=')}`;

      onGetStatuses(query);
    }
  }, [transactions]);

  useEffect(() => {
    if (!isEqual(prevTransactions, transactions) && expandedTransactionId) {
      getGeneralLedgerAction(expandedTransactionId);
    }
  }, [prevTransactions, transactions, expandedTransactionId]);

  useEffect(() => {
    const savedFiltersJSON = localStorage.getItem('exchange-filters');

    if (savedFiltersJSON) {
      const parsed = JSON.parse(savedFiltersJSON);

      if (parsed) {
        const currentEmployeeSavedFilters = parsed?.filter(
          (item: any) => item.employeeId === userId,
        );

        setSavedFilters(currentEmployeeSavedFilters);
      }
    }
  }, [userId]);

  let content: React.JSX.Element;

  const formattedData = useMemo(() => {
    const data = formatExchangeList(
      transactions as BaseTransaction[],
      currenciesList,
    );

    return data;
  }, [transactions, currenciesList]);

  if (isLoading) {
    content = (
      <div className={cn.exchangeLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        title="Exchange"
        columns={columns}
        data={formattedData || []}
        onSort={(name) => {
          onSort(name);
          onClearTimeout();
        }}
        filters={filters}
        sort={sort}
        filtersContent={
          <div>
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_LIST,
            ) && (
              <div className={cn.defaultFilters}>
                <button
                  type="button"
                  className={`${cn.defaultFilterButton} ${
                    statusFilterState === 'in-progress' ? cn.active : ''
                  }`}
                  onClick={() => onInProgressClick()}
                >
                  In progress
                </button>
                <button
                  type="button"
                  className={`${cn.defaultFilterButton} ${
                    statusFilterState === 'unsigned' ? cn.active : ''
                  }`}
                  onClick={() => onUnsignedClick()}
                >
                  Not signed
                </button>
                <button
                  type="button"
                  className={`${cn.defaultFilterButton} ${
                    statusFilterState === 'rejected' ? cn.active : ''
                  }`}
                  onClick={() => onRejectedClick()}
                >
                  Rejected
                </button>
                {settings?.find(
                  (item) => item.key === 'EXCHANGE-CUSTOM-RATE-ENABLED',
                )?.data.value && (
                  <button
                    type="button"
                    className={`${cn.defaultFilterButton} ${
                      statusFilterState === 'rate-request' ? cn.active : ''
                    }`}
                    onClick={() => onRateRequestClick()}
                  >
                    Rate request
                  </button>
                )}

                <button
                  type="button"
                  className={`${cn.defaultFilterButton} ${
                    statusFilterState === 'all' ? cn.active : ''
                  }`}
                  onClick={() => onAllClick()}
                >
                  All
                </button>
              </div>
            )}
          </div>
        }
        topContent={
          <div className={cn.tableTopContent}>
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_CREATE_ACTION,
            ) && (
              <NavLink to={`${ROUTES.EXCHANGE_CREATE}${search || ''}`}>
                <ButtonPrimary
                  text="New Exchange"
                  size={ButtonSize.SMALL}
                  icon={<IconExpand />}
                />
              </NavLink>
            )}
            {unsignedTransactionsList?.length > 0 && (
              <ButtonSecondary
                className={`button-white ${cn.batchSignButton}`}
                text={
                  sendCodeInProgress
                    ? 'Loading...'
                    : `Sign transactions (${unsignedTransactionsList.length})`
                }
                size={ButtonSize.SMALL}
                onClick={onSendCode}
              />
            )}
            {activePermissions?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_LIST,
            ) && (
              <ButtonPseudo
                text={exportIsLoading ? 'Loading...' : 'Export transactions'}
                icon={<IconDownload />}
                className={`button-white ${cn.exportButton}`}
                size={ButtonSize.SMALL}
                onClick={onExportClick}
              />
            )}
          </div>
        }
        filtersComponent={filtersComponent}
        columnsLabels={columnsLabels}
        onFilterChange={(values) => {
          setSelectedSavedFilter('');

          const filterValues: Filters = {
            ...values,
            take: {
              value: [(selectedTake as number)?.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          if (includeRemoved) {
            filterValues.includeRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          if (onlyRemoved) {
            filterValues.onlyRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          onFilter(filterValues);

          if (!values?.baseellaStatus?.value) {
            setStatusFilterState('all');
          }

          if (
            values.baseellaStatus?.value &&
            filters.baseellaStatus?.value &&
            (!isEqual(
              values.baseellaStatus.value,
              filters.baseellaStatus.value,
            ) ||
              selectedSavedFilter)
          ) {
            const sortedDefaultStatusesInProgress =
              defaultInprogressStatuses.sort((a, b) => a?.localeCompare(b));

            const sortedValues = values.baseellaStatus?.value.sort((a, b) =>
              a?.localeCompare(b),
            );

            if (isEqual(sortedValues, sortedDefaultStatusesInProgress)) {
              setStatusFilterState('in-progress');
            } else if (isEqual(sortedValues, [BaseellaStatus.NOT_SIGNED])) {
              setStatusFilterState('unsigned');
            } else if (
              isEqual(sortedValues, [
                BaseellaStatus.AML_REJ,
                BaseellaStatus.REJECTED,
              ])
            ) {
              setStatusFilterState('rejected');
            } else {
              setStatusFilterState('all');
            }
          }
          onClearTimeout();
        }}
        handleSubmit={handleSubmit}
        isLoading={
          isLoading ||
          exchangeListFetching ||
          !selectedTake ||
          transactions === undefined
        }
        tableClassName={!transactions?.length ? cn.emptyTable : ''}
        tableBodyClassName={cn.exchangeTableBody}
        expandableColumns={expandableColumns}
        storeKey="exchange-transaction"
        pagination={pagination}
        setPage={(data) => {
          const filterValues: Filters = {
            ...filters,
            take: {
              value: [(selectedTake as number)?.toString()],
              type: SchemaFilterType.STRING,
            },
          };

          if (includeRemoved) {
            filterValues.includeRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          if (onlyRemoved) {
            filterValues.onlyRemoved = {
              value: ['true'],
              type: SchemaFilterType.STRING,
            };
          }

          setPage({ ...data, filters: filterValues });
          onClearTimeout();
        }}
        take={(selectedTake as number)?.toString()}
        hiddenFiltersTags={
          statusFilterState !== 'all' ? ['baseellaStatus'] : []
        }
        hiddedAllFilterTags={Boolean(selectedSavedFilter)}
        expandable
        withSettings
        withScroll
        error={errorsList || errorList}
        onExpand={getGeneralLedgerAction}
        isLoadingExpandableRow={glIsLoading || isFetching}
        isSuccessExpandableRow={isSuccess}
        paginationDisabled={exchangeListFetching}
        onClearAutoComplete={onClearAutoComplete}
        collapseAllRows={collapseAllRows}
        isFetching={exchangeListFetching}
        onDuplicateClick={(id) => onDuplicate(id)}
        disabledRowIds={disabledRowIds}
        onRowsSelect={(ids) => setUnsignedTransactionsList(ids)}
        clearSelectedItems={createSignatureIsSuccess}
        savedFilters={savedFilters}
        setSavedFilters={setSavedFilters}
        savedFiltersKey="exchange-filters"
        selectedSavedFilter={selectedSavedFilter}
        onSavedFilterClick={onSavedFilterClick}
        setSelectedSavedFilter={setSelectedSavedFilter}
        userId={userId}
        transactionTable
        withDuplicateButton
        withVerticalScroll
        clickable
      />
    );
  }

  return (
    <div className={cn.exchangeWrapper} ref={scrollRef}>
      {content}
      <SignaturePopup
        signaturePopupVisible={signaturePopupVisible}
        validateCode={validateCodeAction}
        validateCodeInProgress={
          validateCodeInProgress || createSignatureInProgress
        }
        errorMessageSignature={errorMessageSignature}
        onSignatureCancelClick={handleClose}
      />
      <div id="signature-popup" />
    </div>
  );
};

export default ExchangeList;
